import { useState } from "react";
import { Group, Box, Text, ScrollArea, Stack, Button } from "@mantine/core";

// Dual-pane transfer list for joint selection (MoveIt-style)
export function DualPaneJointSelector({
  availableJoints,
  selectedJoints,
  onSelectionChange,
  onHighlightJoint,
}: {
  availableJoints: Array<{ name: string; display_name: string | null }>;
  selectedJoints: string[];
  onSelectionChange: (joints: string[]) => void;
  onHighlightJoint: (jointName: string) => void;
}) {
  const [leftSelection, setLeftSelection] = useState<string[]>([]);
  const [rightSelection, setRightSelection] = useState<string[]>([]);

  const unselectedJoints = availableJoints.filter((j) => !selectedJoints.includes(j.name));

  const getDisplayName = (uniqueName: string, displayName: string | null): string => {
    if (displayName) return displayName;
    // Show full unique name including prefix (prefix/joint_name)
    return uniqueName;
  };

  const moveToSelected = () => {
    onSelectionChange([...selectedJoints, ...leftSelection]);
    setLeftSelection([]);
  };

  const moveAllToSelected = () => {
    onSelectionChange([...selectedJoints, ...unselectedJoints.map((j) => j.name)]);
    setLeftSelection([]);
  };

  const moveToAvailable = () => {
    onSelectionChange(selectedJoints.filter((j) => !rightSelection.includes(j)));
    setRightSelection([]);
  };

  const moveAllToAvailable = () => {
    onSelectionChange([]);
    setRightSelection([]);
  };

  const toggleLeftSelection = (jointName: string, shiftKey: boolean) => {
    if (shiftKey && leftSelection.length > 0) {
      // Shift+click: select range
      const jointNames = unselectedJoints.map((j) => j.name);
      const lastIdx = jointNames.indexOf(leftSelection[leftSelection.length - 1]);
      const currentIdx = jointNames.indexOf(jointName);
      const start = Math.min(lastIdx, currentIdx);
      const end = Math.max(lastIdx, currentIdx);
      const rangeJoints = jointNames.slice(start, end + 1);
      setLeftSelection((prev) => [...new Set([...prev, ...rangeJoints])]);
    } else {
      setLeftSelection((prev) =>
        prev.includes(jointName) ? prev.filter((j) => j !== jointName) : [...prev, jointName]
      );
    }
    onHighlightJoint(jointName);
  };

  const toggleRightSelection = (jointName: string, shiftKey: boolean) => {
    if (shiftKey && rightSelection.length > 0) {
      const lastIdx = selectedJoints.indexOf(rightSelection[rightSelection.length - 1]);
      const currentIdx = selectedJoints.indexOf(jointName);
      const start = Math.min(lastIdx, currentIdx);
      const end = Math.max(lastIdx, currentIdx);
      const rangeJoints = selectedJoints.slice(start, end + 1);
      setRightSelection((prev) => [...new Set([...prev, ...rangeJoints])]);
    } else {
      setRightSelection((prev) =>
        prev.includes(jointName) ? prev.filter((j) => j !== jointName) : [...prev, jointName]
      );
    }
    onHighlightJoint(jointName);
  };

  return (
    <Group gap="xs" align="stretch" style={{ height: 250 }}>
      {/* Available Joints (Left) */}
      <Box style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Text size="xs" fw={500} mb={4}>Available Joints ({unselectedJoints.length})</Text>
        <ScrollArea style={{ flex: 1, border: "1px solid var(--mantine-color-gray-3)", borderRadius: 4 }}>
          <Stack gap={0} p={4}>
            {unselectedJoints.map((joint) => {
              const displayName = getDisplayName(joint.name, joint.display_name);
              const isSelected = leftSelection.includes(joint.name);
              return (
                <Box
                  key={joint.name}
                  px={6}
                  py={2}
                  style={{
                    backgroundColor: isSelected ? "var(--mantine-color-blue-1)" : undefined,
                    cursor: "pointer",
                    borderRadius: 2,
                  }}
                  onClick={(e) => toggleLeftSelection(joint.name, e.shiftKey)}
                  onMouseEnter={() => onHighlightJoint(joint.name)}
                >
                  <Text size="xs">{displayName}</Text>
                </Box>
              );
            })}
          </Stack>
        </ScrollArea>
      </Box>

      {/* Transfer Buttons */}
      <Stack gap={4} justify="center">
        <Button size="xs" variant="light" px={8} onClick={moveToSelected} disabled={leftSelection.length === 0}>
          &gt;
        </Button>
        <Button size="xs" variant="light" px={8} onClick={moveAllToSelected} disabled={unselectedJoints.length === 0}>
          &gt;&gt;
        </Button>
        <Button size="xs" variant="light" px={8} onClick={moveToAvailable} disabled={rightSelection.length === 0}>
          &lt;
        </Button>
        <Button size="xs" variant="light" px={8} onClick={moveAllToAvailable} disabled={selectedJoints.length === 0}>
          &lt;&lt;
        </Button>
      </Stack>

      {/* Selected Joints (Right) */}
      <Box style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Text size="xs" fw={500} mb={4}>Selected Joints ({selectedJoints.length})</Text>
        <ScrollArea style={{ flex: 1, border: "1px solid var(--mantine-color-blue-3)", borderRadius: 4, backgroundColor: "var(--mantine-color-blue-0)" }}>
          <Stack gap={0} p={4}>
            {selectedJoints.map((jointName) => {
              const joint = availableJoints.find((j) => j.name === jointName);
              const displayName = joint ? getDisplayName(jointName, joint.display_name) : jointName;
              const isSelected = rightSelection.includes(jointName);
              return (
                <Box
                  key={jointName}
                  px={6}
                  py={2}
                  style={{
                    backgroundColor: isSelected ? "var(--mantine-color-blue-2)" : undefined,
                    cursor: "pointer",
                    borderRadius: 2,
                  }}
                  onClick={(e) => toggleRightSelection(jointName, e.shiftKey)}
                  onMouseEnter={() => onHighlightJoint(jointName)}
                >
                  <Text size="xs">{displayName}</Text>
                </Box>
              );
            })}
          </Stack>
        </ScrollArea>
      </Box>
    </Group>
  );
}

